/**
 * OWL2 Data Range Types
 *
 * Defines data ranges used in OWL2 for constraining data values.
 * Includes datatype references, boolean combinations, enumerations,
 * and datatype restrictions with facets.
 */
// =============================================================================
// Factory Functions
// =============================================================================
/**
 * Create a named datatype reference.
 */
export function datatype(iri) {
    return { type: 'Datatype', iri };
}
/**
 * Create an intersection of data ranges.
 * Requires at least 2 data ranges.
 */
export function dataIntersectionOf(first, second, ...rest) {
    return {
        type: 'DataIntersectionOf',
        dataRanges: [first, second, ...rest]
    };
}
/**
 * Create a union of data ranges.
 * Requires at least 2 data ranges.
 */
export function dataUnionOf(first, second, ...rest) {
    return {
        type: 'DataUnionOf',
        dataRanges: [first, second, ...rest]
    };
}
/**
 * Create a complement of a data range.
 */
export function dataComplementOf(dataRange) {
    return { type: 'DataComplementOf', dataRange };
}
/**
 * Create an enumeration of literal values.
 * Requires at least 1 literal.
 */
export function dataOneOf(first, ...rest) {
    return {
        type: 'DataOneOf',
        literals: [first, ...rest]
    };
}
/**
 * Create a facet restriction.
 */
export function facetRestriction(facet, value) {
    return { facet, value };
}
/**
 * Create a datatype with facet restrictions.
 * Requires at least 1 facet restriction.
 */
export function datatypeRestriction(datatypeRef, firstRestriction, ...restRestrictions) {
    return {
        type: 'DatatypeRestriction',
        datatype: datatypeRef,
        facetRestrictions: [firstRestriction, ...restRestrictions]
    };
}
// =============================================================================
// Type Guards
// =============================================================================
/**
 * Check if a data range is a named datatype.
 */
export function isDatatype(dr) {
    return dr.type === 'Datatype';
}
/**
 * Check if a data range is an intersection.
 */
export function isDataIntersectionOf(dr) {
    return dr.type === 'DataIntersectionOf';
}
/**
 * Check if a data range is a union.
 */
export function isDataUnionOf(dr) {
    return dr.type === 'DataUnionOf';
}
/**
 * Check if a data range is a complement.
 */
export function isDataComplementOf(dr) {
    return dr.type === 'DataComplementOf';
}
/**
 * Check if a data range is an enumeration.
 */
export function isDataOneOf(dr) {
    return dr.type === 'DataOneOf';
}
/**
 * Check if a data range is a datatype restriction.
 */
export function isDatatypeRestriction(dr) {
    return dr.type === 'DatatypeRestriction';
}
// =============================================================================
// Utility Functions
// =============================================================================
/**
 * Get all datatype IRIs referenced in a data range (recursive).
 */
export function getDatatypeIRIs(dr) {
    switch (dr.type) {
        case 'Datatype':
            return [dr.iri];
        case 'DataIntersectionOf':
        case 'DataUnionOf':
            return dr.dataRanges.flatMap(getDatatypeIRIs);
        case 'DataComplementOf':
            return getDatatypeIRIs(dr.dataRange);
        case 'DataOneOf':
            return [];
        case 'DatatypeRestriction':
            return [dr.datatype.iri];
    }
}
